import {register, CloudObject, defineProperty} from 'olympe';
import {PurchaseRequest} from '../PurchaseRequest';
import {tagToDisplayER} from '../../utils/tagTransforms';
import {convertOldPRSourceToLabelValue} from '../../utils/sourceMappings';

/**
 * @extends {CloudObject}
 */
export default class PurchaseRequestDM extends CloudObject {
    /**
     * Creates an instance of PurchaseRequestDM.
     *
     * @param {!Transaction} transaction
     * @return {!string} the tag of the created instance
     */
    static create(transaction) {
        return transaction.create(PurchaseRequestDM);
    }

    /**
     * @param {string} prJson
     * @param {PurchaseRequestDM | Tag | string} purchaseRequest
     * @param {Transaction} transaction
     * @param {string=} tag
     */
    static updatePurchaseRequestFromJson(prJson, purchaseRequest, transaction, tag) {
        const pr = new PurchaseRequest(prJson);

        const formatDate = (dateString) => {
            if (!dateString || isNaN(new Date(dateString))) {
                return;
            }
            // Convert date in Date object
            return new Date(dateString);
        };

        const newSource = convertOldPRSourceToLabelValue(pr.source, pr.sourceFromApiKey, pr.sourceToDisplay);

        PurchaseRequestDM.setJson(transaction, purchaseRequest, prJson);
        PurchaseRequestDM.setCreationDate(transaction, purchaseRequest, formatDate(pr.creationDate));
        PurchaseRequestDM.setSapPrNumber(transaction, purchaseRequest, pr.prNumber);
        PurchaseRequestDM.setL1Category(transaction, purchaseRequest, pr.materials[0].l1Category);
        PurchaseRequestDM.setMaterial(transaction, purchaseRequest, pr.materials[0].name);
        PurchaseRequestDM.setStatus(transaction, purchaseRequest, pr.status);
        PurchaseRequestDM.setSource(transaction, purchaseRequest, pr.source);
        PurchaseRequestDM.setPRSourceLabel(transaction, purchaseRequest, pr.prSource?.label || newSource.label);
        PurchaseRequestDM.setPRSourceValue(transaction, purchaseRequest, pr.prSource?.value || newSource.value);
        PurchaseRequestDM.setSupplierStatus(transaction, purchaseRequest, pr.headerDetails.supplierStatus);
        PurchaseRequestDM.setRequestedDeliveryDate(transaction, purchaseRequest, formatDate(pr.headerDetails.defaultRequestedDeliveryDate));
        PurchaseRequestDM.setEprid(transaction, purchaseRequest, parseInt(pr.EPRId || tagToDisplayER(tag), 10));
        PurchaseRequestDM.setProjectTitle(transaction, purchaseRequest, pr.projectTitle);
        PurchaseRequestDM.setSubmissionDate(transaction, purchaseRequest, formatDate(pr.submissionDate));
        PurchaseRequestDM.setSpenderUserId(transaction, purchaseRequest, pr.spender.value);
        PurchaseRequestDM.setSpenderFullName(transaction, purchaseRequest, pr.spender.label);
        PurchaseRequestDM.setSpenderEmail(transaction, purchaseRequest, pr.spender.email);
        PurchaseRequestDM.setRequesterUserId(transaction, purchaseRequest, pr.requester.value);
        PurchaseRequestDM.setRequesterFullName(transaction, purchaseRequest, pr.requester.label);
        PurchaseRequestDM.setRequesterEmail(transaction, purchaseRequest, pr.requester.email);
        PurchaseRequestDM.setWayOfBuyingLabel(transaction, purchaseRequest, pr.wayOfBuying.currentValue.label);
        PurchaseRequestDM.setWayOfBuyingValue(transaction, purchaseRequest, pr.wayOfBuying.currentValue.value);
        PurchaseRequestDM.setCompanyCodeLabel(transaction, purchaseRequest, pr.headerDetails.companyCode.label);
        PurchaseRequestDM.setCompanyCodeValue(transaction, purchaseRequest, pr.headerDetails.companyCode.value);
        PurchaseRequestDM.setPurchaseOrgLabel(transaction, purchaseRequest, pr.headerDetails.purchaseOrganization.label);
        PurchaseRequestDM.setPurchaseOrgValue(transaction, purchaseRequest, pr.headerDetails.purchaseOrganization.value);
        PurchaseRequestDM.setPurchasingGroupLabel(transaction, purchaseRequest, pr.headerDetails.purchasingGroup.label);
        PurchaseRequestDM.setPurchasingGroupValue(transaction, purchaseRequest, pr.headerDetails.purchasingGroup.value);
        PurchaseRequestDM.setPlantLabel(transaction, purchaseRequest, pr.headerDetails.defaultPlant.label);
        PurchaseRequestDM.setPlantValue(transaction, purchaseRequest, pr.headerDetails.defaultPlant.value);
        PurchaseRequestDM.setStorageLocationLabel(transaction, purchaseRequest, pr.headerDetails.defaultStorageLocation.label);
        PurchaseRequestDM.setStorageLocationValue(transaction, purchaseRequest, pr.headerDetails.defaultStorageLocation.value);
        PurchaseRequestDM.setPaymentTermsLabel(transaction, purchaseRequest, pr.headerDetails.paymentTerms.label);
        PurchaseRequestDM.setPaymentTermsValue(transaction, purchaseRequest, pr.headerDetails.paymentTerms.value);
        PurchaseRequestDM.setSupplierCode(transaction, purchaseRequest, pr.vendorCode);
        PurchaseRequestDM.setSupplierName(transaction, purchaseRequest, pr.vendorName);
        PurchaseRequestDM.setSourcingEventId(transaction, purchaseRequest, pr.sourcingEventID);
        PurchaseRequestDM.setStrategicBuyers(transaction, purchaseRequest, pr.sbValidation.strategicBuyers?.map((sb) => sb.value).join(';').concat(';'));
    };

    /**
     * @return {string}
     */
    getJson() {
        return this.get(PurchaseRequestDM.jsonProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeJson($) {
        return this.observe($, PurchaseRequestDM.jsonProp);
    }

    /**
     * @return {Date}
     */
    getCreationDate() {
        return this.get(PurchaseRequestDM.creationDateProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<Date>}
     */
    observeCreationDate($) {
        return this.observe($, PurchaseRequestDM.creationDateProp);
    }

    /**
     * @return {string}
     */
    getSapPrNumber() {
        return this.get(PurchaseRequestDM.sapPrNumberProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSapPrNumber($) {
        return this.observe($, PurchaseRequestDM.sapPrNumberProp);
    }

    /**
     * @return {string}
     */
    getL1Category() {
        return this.get(PurchaseRequestDM.l1CategoryProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeL1Category($) {
        return this.observe($, PurchaseRequestDM.l1CategoryProp);
    }

    /**
     * @return {string}
     */
    getMaterial() {
        return this.get(PurchaseRequestDM.materialProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeMaterial($) {
        return this.observe($, PurchaseRequestDM.materialProp);
    }

    /**
     * @return {string}
     */
    getStatus() {
        return this.get(PurchaseRequestDM.statusProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeStatus($) {
        return this.observe($, PurchaseRequestDM.statusProp);
    }

    /**
     * @return {string}
     */
    getSource() {
        return this.get(PurchaseRequestDM.sourceProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSource($) {
        return this.observe($, PurchaseRequestDM.sourceProp);
    }

    /**
     * @return {string}
     */
    getSupplierStatus() {
        return this.get(PurchaseRequestDM.supplierStatusProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSupplierStatus($) {
        return this.observe($, PurchaseRequestDM.supplierStatusProp);
    }

    /**
     * @return {Date}
     */
    getRequestedDeliveryDate() {
        return this.get(PurchaseRequestDM.requestedDeliveryDateProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<Date>}
     */
    observeRequestedDeliveryDate($) {
        return this.observe($, PurchaseRequestDM.requestedDeliveryDateProp);
    }

    /**
     * @return {number}
     */
    getEprid() {
        return this.get(PurchaseRequestDM.epridProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<number>}
     */
    observeEprid($) {
        return this.observe($, PurchaseRequestDM.epridProp);
    }

    /**
     * @return {string}
     */
    getProjectTitle() {
        return this.get(PurchaseRequestDM.projectTitleProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeProjectTitle($) {
        return this.observe($, PurchaseRequestDM.projectTitleProp);
    }

    /**
     * @return {Date}
     */
    getSubmissionDate() {
        return this.get(PurchaseRequestDM.submissionDateProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<Date>}
     */
    observeSubmissionDate($) {
        return this.observe($, PurchaseRequestDM.submissionDateProp);
    }

    /**
     * @return {string}
     */
    getSpenderFullName() {
        return this.get(PurchaseRequestDM.spenderFullNameProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSpenderFullName($) {
        return this.observe($, PurchaseRequestDM.spenderFullNameProp);
    }

    /**
     * @return {string}
     */
    getSpenderUserId() {
        return this.get(PurchaseRequestDM.spenderUserIdProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSpenderUserId($) {
        return this.observe($, PurchaseRequestDM.spenderUserIdProp);
    }

    /**
     * @return {string}
     */
    getSpenderEmail() {
        return this.get(PurchaseRequestDM.spenderEmailProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSpenderEmail($) {
        return this.observe($, PurchaseRequestDM.spenderEmailProp);
    }

    /**
     * @return {string}
     */
    getRequesterFullName() {
        return this.get(PurchaseRequestDM.requesterFullNameProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeRequesterFullName($) {
        return this.observe($, PurchaseRequestDM.requesterFullNameProp);
    }

    /**
     * @return {string}
     */
    getRequesterUserId() {
        return this.get(PurchaseRequestDM.requesterUserIdProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeRequesterUserId($) {
        return this.observe($, PurchaseRequestDM.requesterUserIdProp);
    }

    /**
     * @return {string}
     */
    getRequesterEmail() {
        return this.get(PurchaseRequestDM.requesterEmailProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeRequesterEmail($) {
        return this.observe($, PurchaseRequestDM.requesterEmailProp);
    }

    /**
     * @return {string}
     */
    getWayOfBuyingLabel() {
        return this.get(PurchaseRequestDM.wayOfBuyingLabelProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeWayOfBuyingLabel($) {
        return this.observe($, PurchaseRequestDM.wayOfBuyingLabelProp);
    }

    /**
     * @return {string}
     */
    getWayOfBuyingValue() {
        return this.get(PurchaseRequestDM.wayOfBuyingValueProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeWayOfBuyingValue($) {
        return this.observe($, PurchaseRequestDM.wayOfBuyingValueProp);
    }

    /**
     * @return {string}
     */
    getCompanyCodeLabel() {
        return this.get(PurchaseRequestDM.companyCodeLabelProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeCompanyCodeLabel($) {
        return this.observe($, PurchaseRequestDM.companyCodeLabelProp);
    }

    /**
     * @return {string}
     */
    getCompanyCodeValue() {
        return this.get(PurchaseRequestDM.companyCodeValueProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeCompanyCodeValue($) {
        return this.observe($, PurchaseRequestDM.companyCodeValueProp);
    }

    /**
     * @return {string}
     */
    getPurchaseOrgLabel() {
        return this.get(PurchaseRequestDM.purchaseOrgLabelProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePurchaseOrgLabel($) {
        return this.observe($, PurchaseRequestDM.purchaseOrgLabelProp);
    }

    /**
     * @return {string}
     */
    getPurchaseOrgValue() {
        return this.get(PurchaseRequestDM.purchaseOrgValueProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePurchaseOrgValue($) {
        return this.observe($, PurchaseRequestDM.purchaseOrgValueProp);
    }

    /**
     * @return {string}
     */
    getPurchasingGroupLabel() {
        return this.get(PurchaseRequestDM.purchasingGroupLabelProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePurchasingGroupLabel($) {
        return this.observe($, PurchaseRequestDM.purchasingGroupLabelProp);
    }

    /**
     * @return {string}
     */
    getPurchasingGroupValue() {
        return this.get(PurchaseRequestDM.purchasingGroupValueProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePurchasingGroupValue($) {
        return this.observe($, PurchaseRequestDM.purchasingGroupValueProp);
    }

    /**
     * @return {string}
     */
    getPlantLabel() {
        return this.get(PurchaseRequestDM.plantLabelProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePlantLabel($) {
        return this.observe($, PurchaseRequestDM.plantLabelProp);
    }

    /**
     * @return {string}
     */
    getPlantValue() {
        return this.get(PurchaseRequestDM.plantValueProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePlantValue($) {
        return this.observe($, PurchaseRequestDM.plantValueProp);
    }

    /**
     * @return {string}
     */
    getStorageLocationLabel() {
        return this.get(PurchaseRequestDM.storageLocationLabelProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeStorageLocationLabel($) {
        return this.observe($, PurchaseRequestDM.storageLocationLabelProp);
    }

    /**
     * @return {string}
     */
    getStorageLocationValue() {
        return this.get(PurchaseRequestDM.storageLocationValueProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeStorageLocationValue($) {
        return this.observe($, PurchaseRequestDM.storageLocationValueProp);
    }

    /**
     * @return {string}
     */
    getPaymentTermsLabel() {
        return this.get(PurchaseRequestDM.paymentTermsLabelProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePaymentTermsLabel($) {
        return this.observe($, PurchaseRequestDM.paymentTermsLabelProp);
    }

    /**
     * @return {string}
     */
    getPaymentTermsValue() {
        return this.get(PurchaseRequestDM.paymentTermsValueProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observePaymentTermsValue($) {
        return this.observe($, PurchaseRequestDM.paymentTermsValueProp);
    }

    /**
     * @return {string}
     */
    getSupplierCode() {
        return this.get(PurchaseRequestDM.supplierCodeProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSupplierCode($) {
        return this.observe($, PurchaseRequestDM.supplierCodeProp);
    }

    /**
     * @return {string}
     */
    getSupplierName() {
        return this.get(PurchaseRequestDM.supplierNameProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSupplierName($) {
        return this.observe($, PurchaseRequestDM.supplierNameProp);
    }

    /**
     * @return {string}
     */
    getSubmissionStatus() {
        return this.get(PurchaseRequestDM.submissionStatusProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSubmissionStatus($) {
        return this.observe($, PurchaseRequestDM.submissionStatusProp);
    }

    /**
     * @return {string}
     */
    getUsersSeeingPR() {
        return this.get(PurchaseRequestDM.usersSeeingPRProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeUsersSeeingPR($) {
        return this.observe($, PurchaseRequestDM.usersSeeingPRProp);
    }

    /**
     * @return {string}
     */
    getStrategicBuyers() {
        return this.get(PurchaseRequestDM.strategicBuyersProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeStrategicBuyers($) {
        return this.observe($, PurchaseRequestDM.strategicBuyersProp);
    }

    /**
     * @return {string}
     */
    getSourcingEventId() {
        return this.get(PurchaseRequestDM.sourcingEventIdProp);
    }

    /**
     * @param {!BrickContext} $
     * @return {!Observable<string>}
     */
    observeSourcingEventId($) {
        return this.observe($, PurchaseRequestDM.sourcingEventIdProp);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setJson(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.jsonProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {Date} value
     */
    static setCreationDate(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.creationDateProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSapPrNumber(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.sapPrNumberProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setL1Category(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.l1CategoryProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setMaterial(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.materialProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setStatus(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.statusProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSource(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.sourceProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPRSourceLabel(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.prSourceLabelProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPRSourceValue(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.prSourceValueProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSupplierStatus(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.supplierStatusProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {Date} value
     */
    static setRequestedDeliveryDate(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.requestedDeliveryDateProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {number} value
     */
    static setEprid(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.epridProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setProjectTitle(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.projectTitleProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {Date} value
     */
    static setSubmissionDate(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.submissionDateProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSpenderFullName(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.spenderFullNameProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSpenderUserId(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.spenderUserIdProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSpenderEmail(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.spenderEmailProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setRequesterFullName(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.requesterFullNameProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setRequesterUserId(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.requesterUserIdProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setRequesterEmail(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.requesterEmailProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setWayOfBuyingLabel(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.wayOfBuyingLabelProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setWayOfBuyingValue(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.wayOfBuyingValueProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setCompanyCodeLabel(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.companyCodeLabelProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setCompanyCodeValue(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.companyCodeValueProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPurchaseOrgLabel(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.purchaseOrgLabelProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPurchaseOrgValue(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.purchaseOrgValueProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPurchasingGroupLabel(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.purchasingGroupLabelProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPurchasingGroupValue(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.purchasingGroupValueProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPlantLabel(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.plantLabelProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPlantValue(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.plantValueProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setStorageLocationLabel(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.storageLocationLabelProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setStorageLocationValue(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.storageLocationValueProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPaymentTermsLabel(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.paymentTermsLabelProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setPaymentTermsValue(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.paymentTermsValueProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSupplierCode(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.supplierCodeProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSupplierName(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.supplierNameProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSubmissionStatus(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.submissionStatusProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setUsersSeeingPR(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.usersSeeingPRProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setStrategicBuyers(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.strategicBuyersProp, value);
    }

    /**
     * @param {!Transaction} transaction
     * @param {!Tag} instance
     * @param {string} value
     */
    static setSourcingEventId(transaction, instance, value) {
        transaction.update(instance, PurchaseRequestDM.sourcingEventIdProp, value);
    }
}

// Hardcoded tags
register('0196af227c51f4756cf2', PurchaseRequestDM);
PurchaseRequestDM.jsonProp = /** @type {Property<string>} */ defineProperty('0196af2420a28290b82e');
PurchaseRequestDM.creationDateProp = /** @type {Property<Date>} */ defineProperty('0196af2488e9d1edd79b');
PurchaseRequestDM.sapPrNumberProp = /** @type {Property<string>} */ defineProperty('0196af2713b22d0e4cd8');
PurchaseRequestDM.l1CategoryProp = /** @type {Property<string>} */ defineProperty('0196af2810e9cfa3cad1');
PurchaseRequestDM.materialProp = /** @type {Property<string>} */ defineProperty('0196af283b2257c6d4ef');
PurchaseRequestDM.statusProp = /** @type {Property<string>} */ defineProperty('0196af2851c8a3614389');
PurchaseRequestDM.sourceProp = /** @type {Property<string>} */ defineProperty('0196af28755a9821af0e');
PurchaseRequestDM.prSourceLabelProp = /** @type {Property<string>} */ defineProperty('019a9666a73377227b04');
PurchaseRequestDM.prSourceValueProp = /** @type {Property<string>} */ defineProperty('019a9666c7eadfb7c4bf');
PurchaseRequestDM.supplierStatusProp = /** @type {Property<string>} */ defineProperty('0196af2aac616fcfe20d');
PurchaseRequestDM.requestedDeliveryDateProp = /** @type {Property<Date>} */ defineProperty('0196af2b548445473c7e');
PurchaseRequestDM.epridProp = /** @type {Property<number>} */ defineProperty('019735cdab80c4d9e041');
PurchaseRequestDM.projectTitleProp = /** @type {Property<string>} */ defineProperty('0196af2bba65c50c0e80');
PurchaseRequestDM.submissionDateProp = /** @type {Property<Date>} */ defineProperty('0196af2bdf97721d2955');
PurchaseRequestDM.spenderFullNameProp = /** @type {Property<string>} */ defineProperty('0196b3ffb67f2a88bded');
PurchaseRequestDM.spenderUserIdProp = /** @type {Property<string>} */ defineProperty('0196b3ffbb52f4bece23');
PurchaseRequestDM.spenderEmailProp = /** @type {Property<string>} */ defineProperty('0196b3ffbf9ec4626c67');
PurchaseRequestDM.requesterFullNameProp = /** @type {Property<string>} */ defineProperty('0196b3ffc4014145d239');
PurchaseRequestDM.requesterUserIdProp = /** @type {Property<string>} */ defineProperty('0196b3ffc8f32b62e553');
PurchaseRequestDM.requesterEmailProp = /** @type {Property<string>} */ defineProperty('0196b3ffcd4985738bca');
PurchaseRequestDM.wayOfBuyingLabelProp = /** @type {Property<string>} */ defineProperty('0196b3fff62d3141f6c2');
PurchaseRequestDM.wayOfBuyingValueProp = /** @type {Property<string>} */ defineProperty('0196b3fffa27d4999b83');
PurchaseRequestDM.companyCodeLabelProp = /** @type {Property<string>} */ defineProperty('0196b400073923cc973a');
PurchaseRequestDM.companyCodeValueProp = /** @type {Property<string>} */ defineProperty('0196b400094434357d00');
PurchaseRequestDM.purchaseOrgLabelProp = /** @type {Property<string>} */ defineProperty('0196b4000b457e09d461');
PurchaseRequestDM.purchaseOrgValueProp = /** @type {Property<string>} */ defineProperty('0196b4000d1c8c194d4d');
PurchaseRequestDM.purchasingGroupLabelProp = /** @type {Property<string>} */ defineProperty('0196b4000eea8512f690');
PurchaseRequestDM.purchasingGroupValueProp = /** @type {Property<string>} */ defineProperty('0196b40010a6a8ba5e08');
PurchaseRequestDM.plantLabelProp = /** @type {Property<string>} */ defineProperty('0196b40012892d820b29');
PurchaseRequestDM.plantValueProp = /** @type {Property<string>} */ defineProperty('0196b4001451834d8b4c');
PurchaseRequestDM.storageLocationLabelProp = /** @type {Property<string>} */ defineProperty('0196b40016229b1bb3a4');
PurchaseRequestDM.storageLocationValueProp = /** @type {Property<string>} */ defineProperty('0196b400182159dfe580');
PurchaseRequestDM.paymentTermsLabelProp = /** @type {Property<string>} */ defineProperty('0196b4001a717abef99d');
PurchaseRequestDM.paymentTermsValueProp = /** @type {Property<string>} */ defineProperty('0196b4001c9c43146d42');
PurchaseRequestDM.supplierCodeProp = /** @type {Property<string>} */ defineProperty('0196b400206a59ca153f');
PurchaseRequestDM.supplierNameProp = /** @type {Property<string>} */ defineProperty('0196b400254290874ae2');
PurchaseRequestDM.submissionStatusProp = /** @type {Property<string>} */ defineProperty('0196b45e8126ef237999');
PurchaseRequestDM.usersSeeingPRProp = /** @type {Property<string>} */ defineProperty('0196b45ebec580c90624');
PurchaseRequestDM.strategicBuyersProp = /** @type {Property<string>} */ defineProperty('0196c354f9d8bc7a09a3');
PurchaseRequestDM.sourcingEventIdProp = /** @type {Property<string>} */ defineProperty('019716fb688f305105c8');

/**
 * @enum {string}
 */
PurchaseRequestDM.DB_COLUMNS = {
    json: 'json',
    creationDate: 'creation_date',
    sapPrNumber: 'sap_pr_number',
    l1Category: 'l1_category',
    material: 'material',
    status: 'status',
    source: 'source',
    supplierStatus: 'supplier_status',
    requestedDeliveryDate: 'requested_delivery_date',
    eprid: 'epr_id',
    projectTitle: 'project_title',
    submissionDate: 'submission_date',
    spenderFullName: 'spender_full_name',
    spenderUserId: 'spender_user_id',
    spenderEmail: 'spender_email',
    requesterFullName: 'requester_full_name',
    requesterUserId: 'requester_user_id',
    requesterEmail: 'requester_email',
    wayOfBuyingLabel: 'way_of_buying_label',
    wayOfBuyingValue: 'way_of_buying_value',
    companyCodeLabel: 'company_code_label',
    companyCodeValue: 'company_code_value',
    purchaseOrgLabel: 'purchase_org_label',
    purchaseOrgValue: 'purchase_org_value',
    purchasingGroupLabel: 'purchasing_group_label',
    purchasingGroupValue: 'purchasing_group_value',
    plantLabel: 'plant_label',
    plantValue: 'plant_value',
    storageLocationLabel: 'storage_location_label',
    storageLocationValue: 'storage_location_value',
    paymentTermsLabel: 'payment_terms_label',
    paymentTermsValue: 'payment_terms_value',
    supplierCode: 'supplier_code',
    supplierName: 'supplier_name',
    submissionStatus: 'submission_status',
    usersSeeingPR: 'users_seeing_PR',
    strategicBuyers: 'strategic_buyers',
    sourcingEventId: 'sourcing_event_id',
};
